import { useRef, useState } from "react";

import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ProgressModal from "../../components/modals/ProgressModal";

export function OrderDetailsViewModel(logout, orderId, addSnackbar) {
  const [carriers, setCarriers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [order, setOrder] = useState({ invoice_number: "" });
  const [taxRateOptions, setTaxRateOptions] = useState([]);
  const latestOrderDetailsRequest = useRef(0);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      childComponent: (
        <ProgressModal message={message} />
      ),
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const getOrderDetails = async function () {
    const requestId = ++latestOrderDetailsRequest.current;
    const callResult = await makeGetApiCall(`orders/${orderId}`);

    // A slower earlier refresh must not overwrite a newer mutation result.
    if (requestId !== latestOrderDetailsRequest.current) {
      return callResult;
    }

    if (callResult.success) {
      setOrder(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);

    return callResult;
  };

  const getCarriersForDropdown = async function () {
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarriers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getTaxRatesForDropdown = async function () {
    let callResult = await makeGetApiCall("taxRatesForDropdown");
    if (callResult.success) {
      setTaxRateOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const onMakeOrderApiCall = async function (payload) {
    var customer_id = null;
    if (payload.customer) {
      customer_id = payload.customer.value;
    }

    var carrier_id = null;
    if (payload.carrier) {
      carrier_id = payload.carrier.value;
    }

    var status = null;
    if (payload.status) {
      status = payload.status.value;
    }

    const newPayload = {
      ...payload,
      customer_id: customer_id,
      carrier_id: carrier_id,
      status: status,
      memo: payload.memo ?? "",
      invoice_number: payload.invoice_number ?? "",
      truck_question_1: payload.truck_question_1.value,
      truck_question_2: payload.truck_question_2.value,
      truck_question_3: payload.truck_question_3.value,
      truck_question_4: payload.truck_question_4.value,
    };
    let callResult = await makePutApiCall(`orders/${orderId}`, newPayload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Order updated",
          SnackbarType.Success
        )
      );
      getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const updateOrderApiCall = async function (payload) {
    showProgressModal("Updating order...");
    var allOrdersHavePrice = true;

    if (payload.status?.value === 6) {
      for (const carton of payload.mixed_cartons || []) {
        for (const item of carton.source_items || []) {
          if (parseFloat(item.subtotal_price || 0) <= 0) {
            allOrdersHavePrice = false;
          }
        }
      }
    }

    if (allOrdersHavePrice) {
      hideProgressModal();
      await onMakeOrderApiCall(payload);
    } else {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Some products have no price. Review line items or choose Continue to verify anyway.",
          SnackbarType.Error
        )
      );
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            title="Missing unit prices"
            buttonMessage="Continue"
            confirmAction={() => onMakeOrderApiCall(payload)}
            message="One or more order items have no unit cost. Do you want to continue verifying this order?"
            hideReason={true}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const handleOrderItemMutationResult = (callResult, successMessage) => {
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          successMessage,
          SnackbarType.Success,
        ),
      );
      getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onUpdateOrderItem = async function (sourceItem) {
    let callResult = await makePutApiCall(
      `/orders/source-item/update`,
      sourceItem,
    );
    return handleOrderItemMutationResult(callResult, "Item updated");
  };

  const onCreatePriceSplit = async function (sourceItemId, payload) {
    const callResult = await makePostApiCall(
      `/orders/source-items/${sourceItemId}/price-splits`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Price split added");
  };

  const onUpdatePriceSplit = async function (sourceItemId, splitId, payload) {
    const callResult = await makePutApiCall(
      `/orders/source-items/${sourceItemId}/price-splits/${splitId}`,
      payload,
    );
    return handleOrderItemMutationResult(callResult, "Price split updated");
  };

  const onDeletePriceSplit = async function (sourceItemId, splitId) {
    const callResult = await makeDeleteApiCall(
      `/orders/source-items/${sourceItemId}/price-splits/${splitId}`,
    );
    return handleOrderItemMutationResult(callResult, "Price split deleted");
  };

  const onUpdateSourceItemTax = async function (sourceItemId, taxes) {
    const callResult = await makePutApiCall(`/orders/source-item/taxes`, {
      id: sourceItemId,
      taxes: (taxes ?? []).map((tax) => ({ value: tax.value })),
    });
    return handleOrderItemMutationResult(callResult, "Taxes updated");
  };

  const printOrderInvoice = async function () {
    let callResult = await makePostApiCall(`generateOrderInvoice/${orderId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };
  
  const printShippingSummary = async function () {
    let callResult = await makePostApiCall(
      `generateShippingSummaryReport/${orderId}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printShippingDetails = async function () {
    let callResult = await makePostApiCall(
      `generateShippingDetailsReport/${orderId}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  return {
    carriers,
    getCarriersForDropdown,
    customers,
    getCustomersForDropdown,
    loading,
    showSimpleNotification,
    setEmptyModelOpen,
    setShowSimpleNotification,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateOrderApiCall,
    getOrderDetails,
    order,
    onUpdateOrderItem,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    onUpdateSourceItemTax,
    taxRateOptions,
    getTaxRatesForDropdown,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
  };
}
